<template>
  <div>
    <AAlert
      v-if="errorMessage"
      type="error"
      :message="errorMessage"
      banner
      closable
      @close="errorMessage = null"
    />

    <!-- filter -->
    <ARow :gutter="[8, 8]">
      <ACol :xl="6" :md="8" :sm="12" :span="24">
        <FilterDistributor
          style="width:100%"
          :is-tso="hasRoles([ROLE_TSO])"
          v-model:value="state.params.distributor"
        />
      </ACol>

      <ACol :xl="6" :md="8" :sm="12" :span="24">
        <FilterToko
          style="width:100%"
          mode="multiple"
          v-model:value="state.params.toko"
          v-model:vendor_id="state.vendor_id"
        />
      </ACol>

      <ACol :xl="6" :md="8" :sm="12" :span="24">
        <FilterATSalesmanSalesman
          style="width:100%"
          v-model:value="state.params.salesman"
          v-model:useram_id="state.useram_id"
          v-model:vendor_id="state.vendor_id"
        />
      </ACol>

      <!-- [TAMBAH] Filter Kabupaten -->
      <ACol :xl="6" :md="8" :sm="12" :span="24">
        <FilterKabupaten
          style="width:100%"
          v-model:value="state.params.kabupaten"
        />
      </ACol>

      <ACol :xl="6" :md="8" :sm="12" :span="24">
        <AButton
          type="primary"
          @click="fetchDataList"
          :loading="state.isFetching"
        >
          <span v-if="!state.isFetching" class="fa fa-search"/>
          <span v-else>Memuat Data ...</span>
        </AButton>
      </ACol>
    </ARow>

    <!-- export -->
    <div class="row justify-content-end mt-4">
      <div class="col-lg-6"/>
      <div class="col-lg-6 text-right">
        <ASpace>
          <ATooltip title="Download excel">
            <DownloadExcel
              :url="state.endpoint.current"
              :params="queryParams()"
              namefile="Tracking-Penjualan-Toko"
              @errors="errorMessage"
            />
          </ATooltip>
        </ASpace>
      </div>
    </div>

    <!-- table -->
    <div class="table-responsive text-nowrap mt-4">
      <MdTable
        :columns="state.columns"
        :data-source="state.data"
        row-key="key"
        size="small"
        :row-class-name="(record, index) =>
          index % 2 === 1 ? 'table-striped' : null"
        :pagination="{
          showSizeChanger: true,
          pageSizeOptions: ['5','10','20','30','50','75'],
          showTotal: (total, range) =>
            `Menampilkan ${range[0]}-${range[1]} Hasil ${total}`,
          total: state.meta.total,
          pageSize: state.meta.per_page,
          current: state.meta.page,
        }"
        @change="handleTableChange"
        :loading="state.isFetching"
      />
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, reactive, onMounted, computed } from "vue"
import apiClient from "@/services/axios"

import FilterATSalesmanSalesman from "@/components/filter/FilterATSalesmanSalesman"
import FilterToko from "@/components/filter/FilterToko"
import FilterDistributor from "@/components/filter/FilterDistributor"
import FilterKabupaten from "@/components/filter/FilterKabupaten" // [TAMBAH]
import DownloadExcel from "@/components/Molecules/DownloadExcel"

import localStorage from "store"

import {
  hasRoles,
  ROLE_ADMIN_BK,
  ROLE_TSO,
  ROLE_DISTRIBUTOR,
} from "@/helpers"

import {
  updateUrlWithQuery,
  extractQueryParams,
} from "@/helpers/queryparams"

export default defineComponent({
  components: {
    DownloadExcel,
    FilterATSalesmanSalesman,
    FilterToko,
    FilterDistributor,
    FilterKabupaten, // [TAMBAH]
  },

  setup() {

    const errorMessage = ref()

    const user = computed(() => localStorage.get("profile"))

    const state = reactive({
      columns: [],
      data: [],

      endpoint: {
        current: "/api/report-ats/tracking-penjualan-toko",
      },

      meta: {
        per_page: 10,
        page: 1,
        total: 0,
      },

      isFetching: false,

      params: extractQueryParams({
        salesman: [],
        toko: [],
        distributor: [],
        kabupaten: [], // [TAMBAH]
        page: 1,
        "per-page": 10,
      }),

      useram_id: null,
      vendor_id: null,
    })

    const baseColumns = [
      {
        title: "No",
        key: "no",
        width: 40,
        align: "center",
        customRender: ({ index }) =>
          (state.meta.page - 1) * state.meta.per_page + index + 1,
      },
      { title: "Kode Toko", dataIndex: "toko_code" },
      { title: "Nama Toko", dataIndex: "toko_name" },
      { title: "Kabupaten / Kota", dataIndex: "kabupaten" },
      { title: "Kode Distributor", dataIndex: "distributor_code" },
      { title: "Nama Distributor", dataIndex: "distributor_name" },
      { title: "Nama Salesman", dataIndex: "salesman_name" },
      { title: "Nama TSO", dataIndex: "tso" },
    ]

    function generateMonthColumns() {
      const now = new Date()
      const currentMonth = now.getMonth()
      const currentYear = now.getFullYear()

      const months = []

      for (let i = -11; i <= 0; i++) {
        const date = new Date(currentYear, currentMonth + i, 1)

        const monthKey = date.toLocaleString("en-US", { month: "short" }).toLowerCase()
        const monthLabel = date.toLocaleString("en-US", { month: "short" })
        const year = date.getFullYear()

        months.push({
          title: `${monthLabel} ${year}`,
          dataIndex: `${monthKey}_${year}`,
          align: "center",
          width: 60,
          customRender: ({ text }) => Number(text || 0).toFixed(2),
        })
      }

      return months
    }

    function generateAvgLabels() {
      const now = new Date()

      const months = []

      for (let i = -11; i <= 0; i++) {
        const date = new Date(now.getFullYear(), now.getMonth() + i, 1)

        months.push({
          label: date.toLocaleString("en-US", { month: "short" }),
          year: date.getFullYear(),
        })
      }

      const avgStart = `${months[0].label} ${months[0].year}`
      const avgEnd = `${months[10].label} ${months[10].year}`
      const current = `${months[11].label} ${months[11].year}`

      return {
        avgLabel: `${avgStart} s/d ${avgEnd} AVG`,
        selisihLabel: `${current} - AVG`,
      }
    }

    const fetchDataList = () => {
      if (!state.params.distributor || state.params.distributor.length === 0) {
        errorMessage.value = "Distributor wajib dipilih sebelum melakukan pencarian."
        return
      }

      state.isFetching = true

      apiClient
        .get(state.endpoint.current, {
          params: queryParams(),
        })
        .then(({ data }) => {

          const { items, _meta } = data

          state.data = items.map((item, index) => ({
            ...item,
            key: item.toko_id ?? index,
          }))

          state.meta.page = _meta.currentPage
          state.meta.per_page = _meta.perPage
          state.meta.total = _meta.totalCount
        })
        .finally(() => {
          state.isFetching = false
        })
    }

    const handleTableChange = (page) => {

      state.params.page = page.current
      state.params["per-page"] = page.pageSize

      fetchDataList()
    }

    const queryParams = () => {
      return updateUrlWithQuery(state.params)
    }

    onMounted(() => {
      const monthColumns = generateMonthColumns()
      const avgLabels = generateAvgLabels()

      state.columns = [
        ...baseColumns,
        ...monthColumns,
        {
          title: avgLabels.avgLabel,
          dataIndex: "avg",
          align: "center",
          width: 120,
        },
        {
          title: avgLabels.selisihLabel,
          dataIndex: "selisih_avg",
          align: "center",
          width: 90,
        },
      ]

      // fetchDataList()

      if (hasRoles([ROLE_TSO])) {
        state.useram_id = user.value?.id
      }

      if (hasRoles([ROLE_DISTRIBUTOR])) {
        state.vendor_id = user.value?.vendor_id
      }
    })

    return {
      state,
      errorMessage,
      fetchDataList,
      handleTableChange,
      queryParams,

      hasRoles,
      ROLE_ADMIN_BK,
      ROLE_TSO,
      ROLE_DISTRIBUTOR,
    }
  },
})
</script>
